import { createActions, handleActions } from 'redux-actions';
import { createSelector } from 'reselect';

import { CONTENT_ACTION } from '../constants/messageActions';

import * as loadingState from '@coconut/constants/loadingState';

export const types = {
  FETCH_CHATS: 'FETCH_CHATS',
  FETCH_CHATS_FAILED: 'FETCH_CHATS_FAILED',
  FETCH_CHATS_SUCCESS: 'FETCH_CHATS_SUCCESS',

  APPEND_MESSAGE: 'APPEND_MESSAGE',
  UPDATE_MESSAGE: 'UPDATE_MESSAGE',

  DELETE_CURRENT_MESSAGE: 'DELETE_CURRENT_MESSAGE',

  SAVE_QUESTION_DETAILS: 'SAVE_QUESTION_DETAILS',

  NEW_MESSAGE: 'NEW_MESSAGE',
  NEW_MESSAGE_READ: 'NEW_MESSAGE_READ',
  NEW_MESSAGE_READ_FAILED: 'NEW_MESSAGE_READ_FAILED',

  REFETCH_HISTORICAL_MESSAGES: 'REFETCH_HISTORICAL_MESSAGES',
  REFETCH_HISTORICAL_MESSAGES_SUCCESS: 'REFETCH_HISTORICAL_MESSAGES_SUCCESS',
  REFETCH_HISTORICAL_MESSAGES_ERROR: 'REFETCH_HISTORICAL_MESSAGES_ERROR',

  UPDATE_LAST_TUTOR_READ_MESSAGE_ID: 'UPDATE_LAST_TUTOR_READ_MESSAGE_ID',

  SAVE_UN_SENT_MESSAGE: 'SAVE_UN_SENT_MESSAGE',
  SAVE_UN_SENT_MESSAGE_SUCCESS: 'SAVE_UN_SENT_MESSAGE_SUCCESS',
};

const addCurrentMessageAvatar = (previousMessage, currentMessage) => {
  if (
    previousMessage &&
    previousMessage.snapask_uid === currentMessage.snapask_uid
  ) {
    // If the previous mesage action is same
    // as current.
    if (previousMessage.message_action === currentMessage.message_action) {
      return currentMessage;
    }

    if (
      previousMessage.message_action === CONTENT_ACTION &&
      previousMessage.message_action !== currentMessage.message_action
    ) {
      return {
        ...currentMessage,
      };
    }

    return currentMessage;
  }

  return {
    ...currentMessage,
  };
};

export const {
  fetchChats,
  fetchChatsSuccess,
  fetchChatsFailed,
  appendMessage,
  updateMessage,
  newMessage,
  newMessageRead,
  newMessageReadFailed,
  saveQuestionDetails,
  deleteCurrentMessage,
  refetchHistoricalMessages,
  refetchHistoricalMessagesSuccess,
  refetchHistoricalMessagesError,
  updateLastTutorReadMessageId,
  saveUnSentMessage,
  saveUnSentMessageSuccess,
} = createActions(
  {
    [types.FETCH_CHATS_SUCCESS]: ({
      channelName,
      messages,
      lastTutorReadMessageId,
    }) => ({
      channelName,
      messages,
      lastTutorReadMessageId,
    }),
    [types.FETCH_CHATS_FAILED]: (errorMessage) => ({
      errorMessage,
    }),
    [types.REFETCH_HISTORICAL_MESSAGES]: (QID, lang) => ({
      QID,
      lang,
    }),
    [types.REFETCH_HISTORICAL_MESSAGES_SUCCESS]: (messages) => ({
      messages,
    }),
    [types.REFETCH_HISTORICAL_MESSAGES_ERROR]: (errorMessage) => ({
      errorMessage,
    }),
    [types.APPEND_MESSAGE]: (message) => ({
      message,
    }),
    [types.UPDATE_MESSAGE]: ({ newMessageID, message, messageStatus }) => ({
      messageStatus,
      newMessageID,
      message,
    }),
    [types.SAVE_QUESTION_DETAILS]: (questionDetails) => ({
      questionDetails,
    }),
    [types.DELETE_CURRENT_MESSAGE]: (id) => ({
      id,
    }),
    [types.UPDATE_LAST_TUTOR_READ_MESSAGE_ID]: (lastTutorReadMessageId) => ({
      lastTutorReadMessageId,
    }),
    [types.NEW_MESSAGE_READ]: (questionID) => ({
      questionID,
    }),
    [types.NEW_MESSAGE_READ_FAILED]: (errorMessage) => ({
      errorMessage,
    }),
    [types.SAVE_UN_SENT_MESSAGE]: (QID, message) => ({
      QID,
      message,
    }),
    [types.SAVE_UN_SENT_MESSAGE_SUCCESS]: (QID, message) => ({
      QID,
      message,
    }),
  },
  types.FETCH_CHATS,
  types.NEW_MESSAGE
);

const INIT_STATE = {
  questionDetails: [],
  historicalMessages: [],
  currentMessages: [],
  channelName: '',
  loading: loadingState.EMPTY,
  newMessage: false,
  newMessageCount: 0,
  errorMessage: '',
  lastTutorReadMessageId: 0,
  unSentMessage: {},
  isEnableTaggingMessage: true,
};

const reducer = handleActions(
  {
    [types.FETCH_CHATS]: (state) => ({
      ...state,
      loading: loadingState.LOADING,
    }),
    [types.FETCH_CHATS_SUCCESS]: (state, action) => ({
      ...state,
      historicalMessages: action.payload.messages,
      currentMessages: [],
      channelName: action.payload.channelName,
      lastTutorReadMessageId: action.payload.lastTutorReadMessageId,
      loading: loadingState.READY,
    }),
    [types.FETCH_CHATS_FAILED]: (state, action) => ({
      ...state,
      loading: loadingState.ERROR,
      errorMessage: action.payload.errorMessage,
    }),
    [types.REFETCH_HISTORICAL_MESSAGES]: (state) => ({
      ...state,
      loading: loadingState.LOADING,
    }),
    [types.REFETCH_HISTORICAL_MESSAGES_SUCCESS]: (state, action) => ({
      ...state,
      currentMessages: state.currentMessages.filter(
        (message) => message.loadingStatus === loadingState.ERROR
      ),
      historicalMessages: action.payload.messages,
      loading: loadingState.READY,
    }),
    [types.REFETCH_HISTORICAL_MESSAGES_ERROR]: (state, action) => ({
      ...state,
      loading: loadingState.ERROR,
      errorMessage: action.payload.errorMessage,
    }),
    [types.APPEND_MESSAGE]: (state, action) => {
      const { message } = action.payload;

      const previousMessage = state.currentMessages.length
        ? state.currentMessages[state.currentMessages.length - 1]
        : {};

      const messageToAppend = addCurrentMessageAvatar(previousMessage, message);

      return {
        ...state,
        currentMessages: state.currentMessages.concat(messageToAppend),
      };
    },
    [types.SAVE_QUESTION_DETAILS]: (state, action) => ({
      ...state,
      questionDetails: action.payload.questionDetails,
    }),
    [types.NEW_MESSAGE]: (state) => {
      const { newMessageCount } = state;
      return {
        ...state,
        newMessage: true,
        newMessageCount: newMessageCount ? newMessageCount + 1 : 1,
      };
    },
    [types.NEW_MESSAGE_READ]: (state) => ({
      ...state,
      newMessage: false,
      newMessageCount: 0,
    }),
    [types.UPDATE_MESSAGE]: (state, action) => {
      const {
        messageStatus,
        newMessageID,
        message: newMessage,
      } = action.payload;

      return {
        ...state,
        currentMessages: state.currentMessages.map((message) => {
          if (message.id === newMessageID) {
            return {
              ...newMessage,
              loadingStatus: messageStatus,
            };
          }

          return message;
        }),
      };
    },
    [types.DELETE_CURRENT_MESSAGE]: (state, action) => {
      const { id } = action.payload;

      return {
        ...state,
        currentMessages: state.currentMessages.filter(
          (message) => message.id !== id
        ),
      };
    },
    [types.UPDATE_LAST_TUTOR_READ_MESSAGE_ID]: (state, action) => ({
      ...state,
      lastTutorReadMessageId: action.payload.lastTutorReadMessageId,
    }),
    [types.SAVE_UN_SENT_MESSAGE]: (state) => ({
      ...state,
      loading: loadingState.LOADING,
    }),
    [types.SAVE_UN_SENT_MESSAGE_SUCCESS]: (state, action) => {
      const { QID, message } = action.payload;
      const unSentMessage = state.unSentMessage;
      unSentMessage[QID] = message;

      return {
        ...state,
        unSentMessage,
        loading: loadingState.READY,
      };
    },
  },
  INIT_STATE
);

export default reducer;

export const getChatMessage = (state) => state.chat.chatsMessage;
export const getHistoricalMessages = createSelector(
  getChatMessage,
  (chatsMessage) => chatsMessage.historicalMessages
);
export const getQuestionDetails = createSelector(
  getChatMessage,
  (chatsMessage) => chatsMessage.questionDetails
);
export const getCurrentMessages = createSelector(
  getChatMessage,
  (chatsMessage) => chatsMessage.currentMessages || INIT_STATE.currentMessages
);
export const getLoadingStatus = createSelector(
  getChatMessage,
  (chatsMessage) => chatsMessage.loading
);
export const getNewMessageStatus = createSelector(
  getChatMessage,
  (chatsMessage) => chatsMessage.newMessage || INIT_STATE.newMessage
);
export const getNewMessageCount = createSelector(
  getChatMessage,
  (chatsMessage) => chatsMessage.newMessageCount || INIT_STATE.newMessageCount
);
export const getChannelName = createSelector(
  getChatMessage,
  (chatsMessage) => chatsMessage.channelName || INIT_STATE.channelName
);
export const getLastTutorReadMessageId = createSelector(
  getChatMessage,
  (chatsMessage) =>
    chatsMessage.lastTutorReadMessageId || INIT_STATE.lastTutorReadMessageId
);
export const getUnSentMessage = createSelector(
  getChatMessage,
  (chatsMessage) => chatsMessage.unSentMessage
);



// WEBPACK FOOTER //
// ./src/scenes/Chat/services/redux/chatsMessage.js